/**
 * „Notizen" am Kunden — Aufgaben und Notizen in einem Blatt von rechts, neben
 * dem Knopf „Nachrichten" (Benjamin, 24.09.2026).
 *
 * Der Inhalt ist der vorhandene Baustein `NotizenBlock` (dieselben Aufgaben
 * wie auf der Aufgabenseite, am Projekt, am Termin und am Leed) — hier nur
 * oben erreichbar statt am Ende der langen Kundenmaske. Das Eingabefeld hat
 * gleich den Fokus: auf dem Telefon ist die Aufgabe nach einem Tipp getippt.
 */
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { useNotizen } from "../../hooks/useNotizen";
import NotizenBlock from "../notizen/NotizenBlock";
import "../../styles/kunden.css";

const halt = (e: MouseEvent) => e.stopPropagation();

/** `nurZeichen`: nur das Zeichen und die Zahl (schwebende Leiste der Kundenansicht, 03.11.2026). */
export function KundeNotizenKnopf({ kundeId, kundeName, className, symbol, nurZeichen = false }: { kundeId: string; kundeName: string; className?: string; symbol?: ReactNode; nurZeichen?: boolean }) {
  const [offen, setOffen] = useState(false);
  // Derselbe Abruf wie im Block (gleicher Schlüssel) — die Zahl kostet nichts extra.
  const offene = useNotizen("offen", "alle", { kunde_id: kundeId }).data?.length ?? 0;
  return (
    <>
      <button type="button" className={className} onClick={() => setOffen(true)}
        aria-label={nurZeichen ? `Notizen${offene > 0 ? `, ${offene} offen` : ""}` : undefined} title={nurZeichen ? "Notizen" : undefined}>
        {symbol}{!nurZeichen && "Notizen"}{offene > 0 && <span className="k-schnell-zahl" aria-hidden={nurZeichen || undefined} aria-label={nurZeichen ? undefined : `${offene} offen`}>{offene}</span>}
      </button>
      {offen && createPortal(
        <div onClick={halt} onMouseDown={halt}>
          <KundeNotizenBlatt kundeId={kundeId} kundeName={kundeName} onClose={() => setOffen(false)} />
        </div>,
        document.body,
      )}
    </>
  );
}

function KundeNotizenBlatt({ kundeId, kundeName, onClose }: { kundeId: string; kundeName: string; onClose: () => void }) {
  useEffect(() => {
    // Einfangphase + stopPropagation: Escape schließt nur dieses Blatt, nicht
    // das Kundenblatt darunter.
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", zu, true);
    return () => window.removeEventListener("keydown", zu, true);
  }, [onClose]);
  return (
    <div className="k-blatt k-blatt-notizen" role="dialog" aria-modal="true" aria-label={`Notizen zu ${kundeName}`}>
      <button type="button" className="k-blatt-schleier" aria-label="Schließen" onClick={onClose} />
      <aside className="k-blatt-panel">
        <header className="k-blatt-kopf">
          <h2>Notizen · {kundeName}</h2>
          <button type="button" className="k-blatt-zu" aria-label="Schließen" onClick={onClose}>×</button>
        </header>
        <div className="k-blatt-inhalt">
          <NotizenBlock
            bezug={{ kunde_id: kundeId }}
            titel="Aufgaben & Notizen"
            platzhalter="Was ist zu tun? z. B. „Rückruf wegen Termin“"
            fokus
          />
        </div>
      </aside>
    </div>
  );
}
